import React, { useState, useMemo, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, ArrowRight, Layers, Maximize2, Minimize2, Plus, Pencil, CheckCircle, X, AlertTriangle, Check } from 'lucide-react';
import { Gantt, Willow } from '@svar-ui/react-gantt';
import '@svar-ui/react-gantt/all.css';
import useAnalysisStore from '../../../stores/useAnalysisStore';
import useAtelier5Store from '../../../stores/useAtelier5Store';
import useAtelier1Store from '../../../stores/useAtelier1Store';
import AiAssistantButton from '../../common/AiAssistantButton';
import api from '../../../api/apiClient';
import { handleAIError } from '../../../utils/aiErrorHandler';
import { LEVEL_COLOR_BG, LEVEL_COLOR_TEXT, LEVEL_COLOR_CELL, generateDefaultLevels, RiskAcceptanceTable } from '../../../components/common/RiskLevelsDisplay';
import { getTranslatedSeverityLabel } from '../../../constants';
import { buildCoverageRegistry } from './coverageMatrix';

// Parse ISO date strings as local time (date-only strings get local midnight)
const parseISO = (str) => new Date(str.length === 10 ? `${str}T00:00:00` : str);

const getRiskLevelColor = (score, levels) => {
  const level = (levels || []).find(l => score >= l.minScore && score <= l.maxScore);
  if (!level) {
    if (score >= 12) return { bg: 'bg-red-100', text: 'text-red-800', label: 'Critical' };
    if (score >= 8) return { bg: 'bg-orange-100', text: 'text-orange-800', label: 'High' };
    if (score >= 4) return { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Medium' };
    return { bg: 'bg-green-100', text: 'text-green-800', label: 'Low' };
  }
  return { bg: LEVEL_COLOR_BG[level.color] || 'bg-green-100', text: LEVEL_COLOR_TEXT[level.color] || 'text-green-800', label: level.label };
};

const ResidualRiskDisplay = ({ residualRisk, riskLevels }) => {
  if (!residualRisk) return <span className="text-xs text-slate-400">Not evaluated</span>;

  const res = residualRisk.residualRisk || {};
  const lik = res.likelihood || '—';
  const grav = res.gravity || '—';

  const level = (lik !== '—' && grav !== '—') ? getRiskLevelColor(lik * grav, riskLevels) : null;

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${level ? level.bg + ' ' + level.text : 'text-slate-500'}`}>
      L:{lik} × G:{grav}
    </span>
  );
};

const RiskScenarioLegend = ({ riskScenarioIndexMap }) => {
  const entries = Object.entries(riskScenarioIndexMap || {});
  if (!entries.length) return null;
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-2 w-full">
      <h4 className="text-xs font-bold text-slate-800 mb-1.5">Légende</h4>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {entries.map(([id, { shortRef, originalRef, name, scenarioRef }]) => (
          <div key={id} className="text-xs text-slate-700 flex items-center gap-1">
            <span className="font-mono font-semibold text-slate-800 min-w-[24px]">{shortRef}</span>
            <span className="text-slate-300">=</span>
            <span className="truncate max-w-[200px]" title={`${scenarioRef} — ${name || originalRef}`}>{scenarioRef} — {originalRef}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const AddMeasureModal = ({ isOpen, onClose, measure, onSave, responsiblePersonsList, riskScenarioOptions }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '', description: '', category: 'protection', responsiblePerson: '',
    obstacles: '', cost: '', budget: '', estimatedEffort: 0, deadline: '', priority: 'medium', implementation: 'planned', attackPaths: [],
  });

  const fields = [
    { key: 'cost', map: ['—', '+', '++', '+++'], next: v => String(Math.min(3, (Number(v)||0)+1)), prev: v => String(Math.max(0, (Number(v)||0)-1)) },
  ];
  const pMap = ['', 'low', 'medium', 'high', 'critical'];
  const pLabels = ['—', 'P4', 'P3', 'P2', 'P1'];
  const pColors = ['', 'bg-green-500', 'bg-yellow-500', 'bg-orange-500', 'bg-red-500'];

  useEffect(() => {
    if (measure) {
      setFormData({
        name: measure.name || measure.title || '',
        description: measure.description || '',
        category: measure.category || 'protection',
        responsiblePerson: measure.responsiblePerson || measure.responsibleParty || '',
        obstacles: measure.obstacles || '',
        cost: measure.cost ?? '',
        budget: Number(measure.budget) || '',
        estimatedEffort: Number(measure.estimatedEffort) || 0,
        deadline: measure.deadline || '',
        priority: measure.priority || 'medium',
        implementation: measure.implementation || 'planned',
        attackPaths: measure.attackPaths || (measure.attackPathId ? [measure.attackPathId] : []),
      });
    } else {
      setFormData({ name: '', description: '', category: 'protection', responsiblePerson: '', obstacles: '', cost: '', budget: '', estimatedEffort: 0, deadline: '', priority: 'medium', implementation: 'planned', attackPaths: [] });
    }
  }, [measure, isOpen]);

  if (!isOpen) return null;
  const handleSave = () => { onSave(formData); onClose(); };

  return ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
          <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} transition={{ type: "spring", duration: 0.5 }} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">{measure ? t('workshop5RS.editMeasure', 'Modifier la mesure') : t('workshop5RS.addMeasure', 'Ajouter une mesure')}</h3>
              <button onClick={onClose} className="text-white/80 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-6 max-h-[60vh] overflow-y-auto space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">{t('workshop5RS.measure', 'Mesure de sécurité')}</label>
                  <input type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full text-sm border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-400" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">{t('workshop5RS.measureCategory', 'Catégorie')}</label>
                  <select value={formData.category} onChange={e => setFormData({...formData, category: e.target.value})} className="w-full text-sm border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-400">
                    <option value="gouvernance et anticipation">{t('workshop5.category.governance', 'Gouvernance')}</option>
                    <option value="protection">{t('workshop5.category.protection', 'Protection')}</option>
                    <option value="défense">{t('workshop5.category.defense', 'Défense')}</option>
                    <option value="résilience">{t('workshop5.category.resilience', 'Résilience')}</option>
                    <option value="other">{t('workshop5.category.other', 'Autre')}</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-slate-700 mb-1">{t('workshop5RS.description', 'Description')}</label>
                  <textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} rows={2} className="w-full text-sm border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-400" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">{t('workshop5RS.responsible', 'Responsable')}</label>
                  <select value={formData.responsiblePerson} onChange={e => setFormData({...formData, responsiblePerson: e.target.value})} className="w-full text-sm border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-400">
                    <option value="">—</option>
                    {responsiblePersonsList.map(p => (
                      <option key={p.id} value={p.name}>{p.name}{p.role ? ` (${p.role})` : ''}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">{t('workshop5RS.obstacles', 'Freins et difficultés')}</label>
                  <input type="text" value={formData.obstacles} onChange={e => setFormData({...formData, obstacles: e.target.value})} className="w-full text-sm border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-400" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">{t('workshop5RS.costComplexity', 'Coût / Complexité')}</label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono font-bold text-slate-700 min-w-[30px]">{['—', '+', '++', '+++'][Number(formData.cost) || 0]}</span>
                    <button type="button" onClick={() => setFormData({...formData, cost: String(Math.min(3, (Number(formData.cost)||0) + 1))})} className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded">+</button>
                    <button type="button" onClick={() => setFormData({...formData, cost: String(Math.max(0, (Number(formData.cost)||0) - 1))})} className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded">-</button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">{t('workshop5RS.budget', 'Budget (€)')}</label>
                  <input type="number" min="0" value={formData.budget} onChange={e => setFormData({...formData, budget: e.target.value})} className="w-full text-sm border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-400" placeholder="Ex: 50000" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">{t('workshop5RS.estimatedEffort', 'Charge estimée (j/h)')}</label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono font-bold text-slate-700 min-w-[20px]">{Number(formData.estimatedEffort) || 0}</span>
                    <button type="button" onClick={() => setFormData({...formData, estimatedEffort: (Number(formData.estimatedEffort)||0) + 1})} className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded">+</button>
                    <button type="button" onClick={() => setFormData({...formData, estimatedEffort: Math.max(0, (Number(formData.estimatedEffort)||0) - 1)})} className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded">-</button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">{t('workshop5RS.deadline', 'Échéance')}</label>
                  <input type="date" value={formData.deadline} onChange={e => setFormData({...formData, deadline: e.target.value})} className="w-full text-sm border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-400" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">{t('workshop5RS.priority', 'Priorité')}</label>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const idx = pMap.indexOf(formData.priority);
                      const cur = idx >= 0 ? idx : 0;
                      return (
                        <>
                          <span className={`inline-flex items-center justify-center w-8 h-6 rounded text-xs font-bold text-white ${pColors[cur] || 'bg-gray-200 text-gray-500'}`}>{pLabels[cur]}</span>
                          <button type="button" onClick={() => setFormData({...formData, priority: pMap[Math.min(4, cur + 1)]})} className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded">+</button>
                          <button type="button" onClick={() => setFormData({...formData, priority: pMap[Math.max(0, cur - 1)]})} className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded">-</button>
                        </>
                      );
                    })()}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">{t('workshop5RS.status', 'Statut')}</label>
                  <select value={formData.implementation} onChange={e => setFormData({...formData, implementation: e.target.value})} className="w-full text-sm border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-400">
                    <option value="">—</option>
                    <option value="planned">{t('workshop5RS.statusOptions.planned', 'Planifié')}</option>
                    <option value="in_progress">{t('workshop5RS.statusOptions.in_progress', 'En cours')}</option>
                    <option value="implemented">{t('workshop5RS.statusOptions.implemented', 'Implémenté')}</option>
                    <option value="not_planned">{t('workshop5RS.statusOptions.not_planned', 'Non planifié')}</option>
                    <option value="abandoned">{t('workshop5RS.statusOptions.abandoned', 'Abandonné')}</option>
                  </select>
                </div>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-slate-700 mb-1">{t('workshop5RS.associatedScenario', 'Scénario de risque associé')}</label>
                <div className="max-h-32 overflow-y-auto border border-slate-200 rounded p-2 space-y-1">
                  {riskScenarioOptions.map(rs => (
                    <label key={rs.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-50 px-1 py-0.5 rounded">
                      <input type="checkbox" checked={formData.attackPaths?.includes(rs.id)} onChange={(e) => { const cur = formData.attackPaths || []; setFormData({ ...formData, attackPaths: e.target.checked ? [...cur, rs.id] : cur.filter(id => id !== rs.id) }); }} />
                      {rs.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 bg-white border border-slate-200 rounded-lg hover:bg-slate-50">{t('common.cancel', 'Annuler')}</button>
              <button onClick={handleSave} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg">{measure ? t('common.save', 'Enregistrer') : t('workshop5RS.addMeasure', 'Ajouter')}</button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};

const SecurityMeasuresTable = ({ strategicScenarios, riskScenarioIndexMap }) => {
  const { t } = useTranslation();
  const responsiblePersonsList = useAtelier1Store((s) => s.contextData?.responsiblePersonsList) || [];
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');

  const allMeasures = useMemo(() => {
    const seen = new Map();
    for (const ss of (strategicScenarios || [])) {
      const scenarioAttackPathIds = new Set();
      (ss.attackPaths || []).forEach((ap) => {
        const id = typeof ap === 'string' ? ap : ap?.id;
        if (id) scenarioAttackPathIds.add(id);
      });

      const resolveAttackPathRef = (m) => {
        const ids = new Set();
        if (m.attackPathId) ids.add(m.attackPathId);
        if (m.attackPaths?.length) m.attackPaths.forEach((ap) => ids.add(typeof ap === 'string' ? ap : ap));
        const refs = [];
        for (const id of ids) {
          if (riskScenarioIndexMap[id]) refs.push(riskScenarioIndexMap[id].shortRef);
        }
        return refs.length ? refs.join(', ') : null;
      };

      const pushIfNew = (arr, source) => {
        for (const m of (arr || [])) {
          if (!seen.has(m.id)) {
            const enriched = { ...m, source, retained: m.retained !== false, strategicScenarioRef: ss.referenceCode || ss.id, strategicScenarioName: ss.name, strategicScenarioId: ss.id };
            if (source === 'dreadedEvent' && !m.attackPathId && !m.attackPaths?.length && scenarioAttackPathIds.size) {
              enriched.attackPaths = [...scenarioAttackPathIds];
            }
            enriched.displayAttackPathRef = resolveAttackPathRef(enriched);
            seen.set(m.id, enriched);
          }
        }
      };
      pushIfNew(ss.customMeasures, 'custom');
      pushIfNew(ss.dreadedEventMeasures, 'dreadedEvent');
      const ecoMeasures = (ss.ecosystemMeasures || []).filter((m) => {
        const ids = [];
        if (m.attackPaths?.length) m.attackPaths.forEach((ap) => ids.push(typeof ap === 'string' ? ap : ap));
        if (m.attackPathId) ids.push(m.attackPathId);
        return ids.some((id) => scenarioAttackPathIds.has(id));
      });
      pushIfNew(ecoMeasures, 'ecosystem');
    }
    return [...seen.values()];
  }, [strategicScenarios, riskScenarioIndexMap]);

  const riskScenarioOptions = useMemo(() =>
    Object.entries(riskScenarioIndexMap || {}).map(([id, rs]) => ({ id, label: `${rs.shortRef} — ${rs.name}` })),
    [riskScenarioIndexMap]
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [editingMeasure, setEditingMeasure] = useState(null);

  if (!allMeasures.length) return null;

  // Group by category
  const categoryLabels = { governance: t('workshop5.category.governance', 'Gouvernance'), 'gouvernance et anticipation': t('workshop5.category.governance', 'Gouvernance'), protection: t('workshop5.category.protection', 'Protection'), defense: t('workshop5.category.defense', 'Défense'), 'défense': t('workshop5.category.defense', 'Défense'), resilience: t('workshop5.category.resilience', 'Résilience'), 'résilience': t('workshop5.category.resilience', 'Résilience'), other: t('workshop5.category.other', 'Autre') };
  const catColors = {
    governance: 'bg-purple-700 text-white',
    'gouvernance et anticipation': 'bg-purple-700 text-white',
    protection: 'bg-blue-700 text-white',
    defense: 'bg-green-700 text-white',
    'défense': 'bg-green-700 text-white',
    resilience: 'bg-orange-700 text-white',
    'résilience': 'bg-orange-700 text-white',
  };
  const catOrder = ['gouvernance et anticipation', 'governance', 'protection', 'défense', 'defense', 'résilience', 'resilience', 'other'];

  const CAT_ALIASES = { governance: 'gouvernance et anticipation', defense: 'défense', resilience: 'résilience' };
  const grouped = {};
  for (const m of allMeasures) {
    const cat = CAT_ALIASES[m.category] || m.category || 'other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(m);
  }
  const sortedCats = Object.keys(grouped).sort((a, b) => {
    const ai = catOrder.indexOf(a);
    const bi = catOrder.indexOf(b);
    return (ai >= 0 ? ai : 99) - (bi >= 0 ? bi : 99);
  });

  const startEdit = (measureId, field, currentValue) => {
    setEditingCell({ measureId, field });
    setEditValue(currentValue || '');
  };

  const saveEdit = (measureId, field) => {
    if (editingCell && editingCell.measureId === measureId && editingCell.field === field) {
      const store = useAtelier5Store.getState();
      strategicScenarios.forEach((ss) => {
        store.updateMeasureField(ss.id, measureId, field, editValue);
      });
      setEditingCell(null);
      setEditValue('');
    }
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const isEditing = (measureId, field) => editingCell && editingCell.measureId === measureId && editingCell.field === field;

  const renderEditableCell = (measure, field, displayValue, type) => {
    const editing = isEditing(measure.id, field);
    if (editing) {
      return (
        <input
          autoFocus
          type={type || 'text'}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={() => saveEdit(measure.id, field)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') saveEdit(measure.id, field);
            if (e.key === 'Escape') cancelEdit();
          }}
          className="w-full px-1.5 py-1 text-xs border border-blue-400 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
        />
      );
    }
    return (
      <span
        className={`cursor-pointer hover:bg-slate-100 px-1.5 py-1 rounded -mx-1.5 block min-h-[20px] ${displayValue ? '' : 'text-slate-300 italic'}`}
        onClick={() => startEdit(measure.id, field, displayValue)}
        title={displayValue || t('workshop5RS.clickToEdit')}
      >
        {displayValue || '—'}
      </span>
    );
  };

  const handleAddClick = () => { setEditingMeasure(null); setModalOpen(true); };

  const handleEditClick = (measure) => { setEditingMeasure(measure); setModalOpen(true); };

  const handleToggleRetained = (measure) => {
    const store = useAtelier5Store.getState();
    const newValue = !measure.retained;
    strategicScenarios.forEach((ss) => {
      ['customMeasures', 'dreadedEventMeasures', 'ecosystemMeasures'].forEach((key) => {
        const arr = ss[key] || [];
        if (arr.some((m) => String(m.id) === String(measure.id))) {
          store.updateMeasureField(ss.id, measure.id, 'retained', newValue);
        }
      });
    });
  };

  const handleModalSave = (formData) => {
    const store = useAtelier5Store.getState();
    if (editingMeasure) {
      // Edit existing measure - update each field on all scenarios
      Object.entries(formData).forEach(([field, value]) => {
        strategicScenarios.forEach((ss) => store.updateMeasureField(ss.id, editingMeasure.id, field, value));
        if (field === 'name') strategicScenarios.forEach((ss) => store.updateMeasureField(ss.id, editingMeasure.id, 'title', value));
        if (field === 'responsiblePerson') strategicScenarios.forEach((ss) => store.updateMeasureField(ss.id, editingMeasure.id, 'responsibleParty', value));
      });
    } else {
      // Add new custom measure
      const newId = `custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const newMeasure = { id: newId, ...formData, source: 'custom', type: 'custom' };
      strategicScenarios.forEach((ss) => store.addCustomMeasure(ss, newMeasure));
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">{t('workshop5RS.title')}</h3>
        <div className="flex items-center gap-2">
          <button onClick={handleAddClick} className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded transition-colors">
            <Plus size={14} /> {t('workshop5RS.addMeasure', 'Ajouter')}
          </button>
          <span className="text-xs text-slate-500">{t('workshop5RS.measuresCount', { count: allMeasures.length })}</span>
        </div>
      </div>
      <div className="overflow-x-auto p-1">
        <table className="w-full text-xs border border-slate-200">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left px-1.5 py-1.5 font-semibold text-slate-700 text-[10px]">{t('workshop5RS.measure')}</th>
              <th className="text-left px-1.5 py-1.5 font-semibold text-slate-700 text-[10px]">{t('workshop5RS.measureCategory', 'Catégorie')}</th>
              <th className="text-left px-1.5 py-1.5 font-semibold text-slate-700 text-[10px]">{t('workshop5RS.associatedScenario')}</th>
              <th className="text-left px-1.5 py-1.5 font-semibold text-slate-700 text-[10px]">{t('workshop5RS.responsible')}</th>
              <th className="text-left px-1.5 py-1.5 font-semibold text-slate-700 text-[10px]">{t('workshop5RS.obstacles')}</th>
              <th className="text-left px-1.5 py-1.5 font-semibold text-slate-700 text-[10px]">{t('workshop5RS.costComplexity')}</th>
              <th className="text-left px-1.5 py-1.5 font-semibold text-slate-700 text-[10px]">{t('workshop5RS.budget', 'Budget (€)')}</th>
              <th className="text-left px-1.5 py-1.5 font-semibold text-slate-700 text-[10px]">{t('workshop5RS.estimatedEffort')}</th>
              <th className="text-left px-1.5 py-1.5 font-semibold text-slate-700 text-[10px]">{t('workshop5RS.deadline')}</th>
              <th className="text-left px-1.5 py-1.5 font-semibold text-slate-700 text-[10px]">{t('workshop5RS.priority')}</th>
              <th className="text-left px-1.5 py-1.5 font-semibold text-slate-700 text-[10px]">{t('workshop5RS.status')}</th>
              <th className="text-center px-1.5 py-1.5 font-semibold text-slate-700 text-[10px] w-12">{t('workshop5RS.actions', 'Actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sortedCats.map(cat => (
              <React.Fragment key={cat}>
                <tr>
                    <td colSpan={12} className={`px-4 py-0.5 text-xs font-bold tracking-wide text-center ${catColors[cat] || 'bg-gray-700 text-white'}`}>
                    {categoryLabels[cat] || cat} : {t('workshop5RS.measuresCount', { count: grouped[cat].length })}
                  </td>
                </tr>
                {grouped[cat].map(m => (
                  <tr key={m.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-1.5 py-1.5 max-w-[250px] whitespace-normal">
                      <div className="text-[11px] font-medium text-slate-800">
                        {m.title || m.name || m.measureName || m.description || '—'}
                        <span className="text-[9px] text-slate-400 ml-1">
                          ({m.source === 'custom' ? t('workshop5RS.sourceAtelier5') :
                            m.source === 'dreadedEvent' ? t('workshop5RS.sourceAtelier1') :
                            t('workshop5RS.sourceAtelier3')})
                        </span>
                      </div>
                      {m.description && <div className="text-[9px] text-slate-400 mt-0.5 truncate max-w-[160px]" title={m.description}>{m.description}</div>}
                    </td>
                    <td className="px-1.5 py-1.5">
                      <select
                        value={m.category || 'other'}
                        onChange={(e) => { const v = e.target.value; const store = useAtelier5Store.getState(); strategicScenarios.forEach((ss) => store.updateMeasureField(ss.id, m.id, 'category', v)); }}
                        className="text-[10px] border border-slate-200 rounded px-1 py-0.5 pr-5 focus:outline-none focus:ring-1 focus:ring-blue-400 max-w-[100px]"
                      >
                        <option value="gouvernance et anticipation">{t('workshop5.category.governance', 'Gouvernance')}</option>
                        <option value="protection">{t('workshop5.category.protection', 'Protection')}</option>
                        <option value="défense">{t('workshop5.category.defense', 'Défense')}</option>
                        <option value="résilience">{t('workshop5.category.resilience', 'Résilience')}</option>
                        <option value="other">{t('workshop5.category.other', 'Autre')}</option>
                      </select>
                    </td>
                    <td className="px-1.5 py-1.5 text-[10px] text-slate-600">{m.displayAttackPathRef || '—'}</td>
                    <td className="px-1.5 py-1.5">
                      <select
                        value={m.responsibleParty || m.responsiblePerson || ''}
                        onChange={(e) => { const v = e.target.value; const store = useAtelier5Store.getState(); strategicScenarios.forEach((ss) => { store.updateMeasureField(ss.id, m.id, 'responsiblePerson', v); store.updateMeasureField(ss.id, m.id, 'responsibleParty', v); }); }}
                        className="text-[10px] border border-slate-200 rounded px-1 py-0.5 pr-5 focus:outline-none focus:ring-1 focus:ring-blue-400 max-w-[140px]"
                      >
                        <option value="">—</option>
                        {responsiblePersonsList.map((p) => (
                          <option key={p.id} value={p.name}>{p.name}{p.role ? ` (${p.role})` : ''}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-1.5 py-1.5">{renderEditableCell(m, 'obstacles', m.obstacles)}</td>
                    <td className="px-1.5 py-1.5">
                      {(() => {
                        const levels = ['—', '+', '++', '+++'];
                        const idx = [1, 2, 3].includes(Number(m.cost)) ? Number(m.cost) : 0;
                        const change = (delta) => {
                          const next = Math.max(0, Math.min(3, idx + delta));
                          const v = next === 0 ? '' : String(next);
                          const store = useAtelier5Store.getState();
                          strategicScenarios.forEach((ss) => store.updateMeasureField(ss.id, m.id, 'cost', v));
                        };
                        return (
                          <div className="flex items-center gap-1">
                            <span className="text-[10px] font-mono font-bold text-slate-700 min-w-[16px] text-center">{levels[idx]}</span>
                            <div className="flex flex-col items-center gap-0.5">
                              <button onClick={() => change(1)} className="text-[7px] text-slate-500 hover:text-slate-800 leading-none p-0 h-2">▲</button>
                              <button onClick={() => change(-1)} className="text-[7px] text-slate-500 hover:text-slate-800 leading-none p-0 h-2">▼</button>
                            </div>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-1.5 py-1.5">{renderEditableCell(m, 'budget', `${Number(m.budget) || 0} \u20ac`, 'number')}</td>
                    <td className="px-1.5 py-1.5">
                      {(() => {
                        const effort = Number(m.estimatedEffort) || 0;
                        const changeEffort = (delta) => {
                          const next = Math.max(0, effort + delta);
                          const store = useAtelier5Store.getState();
                          strategicScenarios.forEach((ss) => store.updateMeasureField(ss.id, m.id, 'estimatedEffort', next));
                        };
                        return (
                          <div className="flex items-center gap-1">
                            <span className="text-[10px] font-mono font-bold text-slate-700 min-w-[16px] text-center">{effort}</span>
                            <div className="flex flex-col items-center gap-0.5">
                              <button onClick={() => changeEffort(1)} className="text-[7px] text-slate-500 hover:text-slate-800 leading-none p-0 h-2">▲</button>
                              <button onClick={() => changeEffort(-1)} className="text-[7px] text-slate-500 hover:text-slate-800 leading-none p-0 h-2">▼</button>
                            </div>
                            <span className="text-[9px] text-slate-500">j/h</span>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-1.5 py-1.5">{renderEditableCell(m, 'deadline', m.deadline, 'date')}</td>
                    <td className="px-1.5 py-1.5">
                      {(() => {
                        const pMap = ['', 'low', 'medium', 'high', 'critical'];
                        const pLabels = ['—', 'P4', 'P3', 'P2', 'P1'];
                        const pColors = ['', 'bg-green-500', 'bg-yellow-500', 'bg-orange-500', 'bg-red-500'];
                        const idx = pMap.indexOf(m.priority);
                        const cur = idx >= 0 ? idx : 0;
                        const change = (delta) => {
                          const next = Math.max(0, Math.min(4, cur + delta));
                          const store = useAtelier5Store.getState();
                          strategicScenarios.forEach((ss) => store.updateMeasureField(ss.id, m.id, 'priority', pMap[next]));
                        };
                        return (
                          <div className="flex items-center gap-1">
                            <span className={`inline-flex items-center justify-center w-7 h-4 rounded text-[9px] font-bold text-white ${pColors[cur] || 'bg-gray-200 text-gray-500'}`}>
                              {pLabels[cur]}
                            </span>
                            <div className="flex flex-col items-center gap-0.5">
                              <button onClick={() => change(1)} className="text-[7px] text-slate-500 hover:text-slate-800 leading-none p-0 h-2">▲</button>
                              <button onClick={() => change(-1)} className="text-[7px] text-slate-500 hover:text-slate-800 leading-none p-0 h-2">▼</button>
                            </div>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-1.5 py-1.5">
                      <select
                        value={m.implementation || ''}
                        onChange={(e) => { const v = e.target.value; const store = useAtelier5Store.getState(); strategicScenarios.forEach((ss) => store.updateMeasureField(ss.id, m.id, 'implementation', v)); }}
                        className="text-[10px] border border-slate-200 rounded px-1 py-0.5 pr-5 focus:outline-none focus:ring-1 focus:ring-blue-400"
                      >
                        <option value="">—</option>
                        <option value="planned">{t('workshop5RS.statusOptions.planned')}</option>
                        <option value="in_progress">{t('workshop5RS.statusOptions.in_progress')}</option>
                        <option value="implemented">{t('workshop5RS.statusOptions.implemented')}</option>
                        <option value="not_planned">{t('workshop5RS.statusOptions.not_planned')}</option>
                        <option value="abandoned">{t('workshop5RS.statusOptions.abandoned')}</option>
                      </select>
                    </td>
                    <td className="px-1 py-1.5 text-center whitespace-nowrap">
                      <button onClick={() => handleEditClick(m)} className="inline-flex items-center justify-center w-7 h-7 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors" title={t('common.edit', 'Modifier')}><Pencil size={14} /></button>
                      <button
                        onClick={() => handleToggleRetained(m)}
                        className={`inline-flex items-center justify-center w-7 h-7 rounded transition-colors ${
                          m.retained
                            ? 'text-green-600 bg-green-50'
                            : 'text-slate-400 hover:text-green-600 hover:bg-green-50'
                        }`}
                        title={m.retained ? t('workshop5RS.notRetained', 'Non retenu') : t('workshop5RS.retained', 'Retenu')}
                      >
                        <CheckCircle size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <AddMeasureModal isOpen={modalOpen} onClose={() => setModalOpen(false)} measure={editingMeasure} onSave={handleModalSave} responsiblePersonsList={responsiblePersonsList} riskScenarioOptions={riskScenarioOptions} />
    </div>
  );
};

const RiskScenarioRecapTable = ({ strategicScenarios, attackPathScenarios, riskScenarioIndexMap, riskLevels }) => {
  const { t } = useTranslation();

  const ssLookup = useMemo(() => {
    const map = {};
    for (const ss of (strategicScenarios || [])) {
      for (const ap of (ss.attackPaths || [])) {
        const id = typeof ap === 'string' ? ap : ap.id;
        if (id) map[id] = ss;
      }
    }
    return map;
  }, [strategicScenarios]);

  const rows = useMemo(() => {
    return attackPathScenarios.map(ap => {
      const ref = riskScenarioIndexMap[ap.id];
      const ss = ssLookup[ap.id];
      const risk = ap.gravity * ap.likelihood;
      const level = getRiskLevelColor(risk, riskLevels);
      return {
        key: ap.id,
        ref: ref?.shortRef || ap.referenceCode || '',
        scenarioName: ss?.name || '',
        attackPathName: ap.name || '',
        gravity: ap.gravity,
        likelihood: ap.likelihood,
        risk,
        level,
      };
    });
  }, [attackPathScenarios, riskScenarioIndexMap, ssLookup, riskLevels]);

  if (!rows.length) return null;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <Layers size={16} className="text-blue-500" />
          {t('workshop5.synthesis.recapTitle', 'R\u00e9capitulatif des sc\u00e9narios de risque')}
        </h3>
      </div>
      <div className="overflow-x-auto p-1">
        <table className="w-full text-sm border border-slate-200">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left px-3 py-2 font-semibold text-slate-700 text-xs">{t('workshop5.synthesis.recapRef', 'R\u00e9f')}</th>
              <th className="text-left px-3 py-2 font-semibold text-slate-700 text-xs">{t('workshop5.synthesis.recapScenario', 'Sc\u00e9nario strat\u00e9gique')}</th>
              <th className="text-left px-3 py-2 font-semibold text-slate-700 text-xs">{t('workshop5.synthesis.recapAttackPath', 'Voie d\u2019attaque')}</th>
              <th className="text-center px-3 py-2 font-semibold text-slate-700 text-xs">{t('workshop5.synthesis.recapGravity', 'G')}</th>
              <th className="text-center px-3 py-2 font-semibold text-slate-700 text-xs">{t('workshop5.synthesis.recapLikelihood', 'V')}</th>
              <th className="text-center px-3 py-2 font-semibold text-slate-700 text-xs">{t('workshop5.synthesis.recapRisk', 'Niveau de risque')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map(row => (
              <tr key={row.key} className="hover:bg-slate-50 transition-colors">
                <td className="px-3 py-2 text-xs font-mono font-bold text-slate-700">{row.ref}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{row.scenarioName}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{row.attackPathName}</td>
                <td className="px-3 py-2 text-xs text-center font-mono text-slate-700">{row.gravity}</td>
                <td className="px-3 py-2 text-xs text-center font-mono text-slate-700">{row.likelihood}</td>
                <td className="px-3 py-2 text-center">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${row.level.bg} ${row.level.text}`}>
                    {row.risk}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AttackPathRiskMatrix = ({ scenarios, maxGravity, t, riskScenarioIndexMap, riskLevels }) => {
  const matrix = Array(maxGravity).fill(null).map(() => Array(4).fill([]));

  scenarios.forEach((s) => {
    const row = maxGravity - s.gravity;
    const col = s.likelihood - 1;
    if (row >= 0 && row < maxGravity && col >= 0 && col < 4) {
      matrix[row][col] = [...matrix[row][col], s];
    }
  });

  const getCellColor = (g, l) => {
    const risk = g * l;
    const level = (riskLevels || []).find(lv => risk >= lv.minScore && risk <= lv.maxScore);
    return level ? (LEVEL_COLOR_CELL[level.color] || 'bg-green-500') : 'bg-green-500';
  };

  const gravityLabels = [];
  for (let i = maxGravity; i >= 1; i--) gravityLabels.push(i);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 p-6"
    >
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Layers className="w-5 h-5 text-blue-600 mr-2" />
          {t('workshop5ResidualCards.initialMatrixTitle')}
        </h3>
        <div className="flex items-center space-x-4">
          <span className="text-sm text-gray-500">{scenarios.length} {t('workshop5.synthesis.riskScenarios.scenarios')}</span>
          {(riskLevels || []).map(lv => (
            <div key={lv.level} className="flex items-center space-x-2 text-sm text-gray-600">
              <div className={`w-3 h-3 rounded ${LEVEL_COLOR_CELL[lv.color] || 'bg-green-500'}`}></div>
              <span>{lv.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="grid gap-1" style={{ gridTemplateColumns: `auto repeat(4, 1fr)` }}>
          <div className="p-2 text-xs font-semibold text-gray-500 text-center">Gravité \ Vraisemblance</div>
          {[1, 2, 3, 4].map((v) => (
            <div key={v} className="p-2 text-xs font-semibold text-gray-500 text-center">V{v}</div>
          ))}

          {gravityLabels.map((g) => (
            <React.Fragment key={g}>
              <div className="p-2 text-xs font-semibold text-gray-600 text-center bg-gray-50 rounded">{'G' + g}</div>
              {[1, 2, 3, 4].map((l) => {
                const cellScenarios = matrix[maxGravity - g][l - 1] || [];
                const cellColor = getCellColor(g, l);
                return (
                  <div
                    key={l}
                    className={`min-h-[60px] rounded p-1.5 flex flex-col items-center justify-start gap-0.5 ${cellColor} ${['bg-red-500','bg-red-600','bg-orange-500'].includes(cellColor) ? 'text-white' : 'text-gray-800'}`}
                  >
                    <span className="text-[10px] font-bold opacity-50">{g * l}</span>
                    {cellScenarios.map((s, i) => (
                      <span key={s.id} className="text-xs font-bold leading-tight truncate w-full text-center cursor-default" title={s.name}>
                        {riskScenarioIndexMap?.[s.id]?.shortRef || s.referenceCode || s.id}
                      </span>
                    ))}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

const ResidualRiskMatrix = ({ attackPathScenarios, riskScenarios, maxGravity, t, riskScenarioIndexMap, riskLevels }) => {
  const residualScenarios = useMemo(() => {
    return attackPathScenarios.map(ap => {
      const rs = riskScenarios.find(r => r.attackPathId === ap.id);
      const residual = rs?.residualRisk || {};
      return {
        ...ap,
        gravity: Number(residual.residualGravity) || ap.gravity,
        likelihood: Number(residual.residualLikelihood) || ap.likelihood,
      };
    });
  }, [attackPathScenarios, riskScenarios]);

  const matrix = Array(maxGravity).fill(null).map(() => Array(4).fill([]));

  residualScenarios.forEach((s) => {
    const row = maxGravity - s.gravity;
    const col = s.likelihood - 1;
    if (row >= 0 && row < maxGravity && col >= 0 && col < 4) {
      matrix[row][col] = [...matrix[row][col], s];
    }
  });

  const getCellColor = (g, l) => {
    const risk = g * l;
    const level = (riskLevels || []).find(lv => risk >= lv.minScore && risk <= lv.maxScore);
    return level ? (LEVEL_COLOR_CELL[level.color] || 'bg-green-500') : 'bg-green-500';
  };

  const gravityLabels = [];
  for (let i = maxGravity; i >= 1; i--) gravityLabels.push(i);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 p-6"
    >
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Layers className="w-5 h-5 text-blue-600 mr-2" />
          {t('workshop5ResidualCards.matrixTitle')}
        </h3>
        <div className="flex items-center space-x-4">
          <span className="text-sm text-gray-500">{residualScenarios.length} {t('workshop5.synthesis.riskScenarios.scenarios')}</span>
          {(riskLevels || []).map(lv => (
            <div key={lv.level} className="flex items-center space-x-2 text-sm text-gray-600">
              <div className={`w-3 h-3 rounded ${LEVEL_COLOR_CELL[lv.color] || 'bg-green-500'}`}></div>
              <span>{lv.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="grid gap-1" style={{ gridTemplateColumns: `auto repeat(4, 1fr)` }}>
          <div className="p-2 text-xs font-semibold text-gray-500 text-center">Gravité \ Vraisemblance</div>
          {[1, 2, 3, 4].map((v) => (
            <div key={v} className="p-2 text-xs font-semibold text-gray-500 text-center">V{v}</div>
          ))}

          {gravityLabels.map((g) => (
            <React.Fragment key={g}>
              <div className="p-2 text-xs font-semibold text-gray-600 text-center bg-gray-50 rounded">{'G' + g}</div>
              {[1, 2, 3, 4].map((l) => {
                const cellScenarios = matrix[maxGravity - g][l - 1] || [];
                const cellColor = getCellColor(g, l);
                return (
                  <div
                    key={l}
                    className={`min-h-[60px] rounded p-1.5 flex flex-col items-center justify-start gap-0.5 ${cellColor} ${['bg-red-500','bg-red-600','bg-orange-500'].includes(cellColor) ? 'text-white' : 'text-gray-800'}`}
                  >
                    <span className="text-[10px] font-bold opacity-50">{g * l}</span>
                    {cellScenarios.map((s, i) => (
                      <span key={s.id} className="text-xs font-bold leading-tight truncate w-full text-center cursor-default" title={s.name}>
                        {riskScenarioIndexMap?.[s.id]?.shortRef || s.referenceCode || s.id}
                      </span>
                    ))}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

const EditableField = ({ value, onSave, placeholder, multiline }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || '');
  useEffect(() => { setVal(value || ''); }, [value]);
  if (editing) {
    const Tag = multiline ? 'textarea' : 'input';
    return (
      <Tag
        autoFocus
        value={val}
        onChange={e => setVal(e.target.value)}
        onBlur={() => { onSave(val); setEditing(false); }}
        onKeyDown={e => {
          if (e.key === 'Enter' && !multiline) { onSave(val); setEditing(false); }
          if (e.key === 'Escape') { setVal(value || ''); setEditing(false); }
        }}
        className="w-full text-xs border border-blue-400 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 resize-none"
        rows={multiline ? 3 : 1}
      />
    );
  }
  return (
    <span
      className="cursor-pointer hover:bg-slate-100 px-1.5 py-0.5 rounded -mx-1.5 block text-xs text-slate-700 min-h-[20px]"
      onClick={() => { setVal(value || ''); setEditing(true); }}
    >
      {value || <span className="text-slate-300 italic">{placeholder || 'Cliquez pour éditer'}</span>}
    </span>
  );
};

const ResidualRiskAiModal = ({ data, onClose, onApply }) => {
  const { t } = useTranslation();
  if (!data) return null;
  const { suggestion, initialG, initialL, shortRef, attackPathName } = data;
  const suggestedLevel = suggestion.residualGravity * suggestion.residualLikelihood;
  return ReactDOM.createPortal(
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
        <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} transition={{ type: 'spring', duration: 0.5 }} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
          <div className="px-6 py-4 bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-between">
            <h3 className="text-lg font-bold text-white">{t('workshop5ResidualCards.aiTitle', 'Assistant ACG — Risque résiduel')} · {shortRef}</h3>
            <button onClick={onClose} className="text-white/80 hover:text-white"><X size={20} /></button>
          </div>
          <div className="p-6 space-y-4">
            <p className="text-xs text-slate-500 truncate">{attackPathName}</p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-slate-100 rounded-lg p-3 text-center">
                <div className="text-[11px] font-semibold text-slate-500 uppercase">{t('workshop5ResidualCards.initialRisk', 'Risque initial')}</div>
                <div className="text-xl font-bold text-slate-800 mt-1">G{initialG} × V{initialL} = {initialG * initialL}</div>
              </div>
              <div className="bg-blue-100 rounded-lg p-3 text-center">
                <div className="text-[11px] font-semibold text-slate-500 uppercase">{t('workshop5ResidualCards.aiSuggestion', 'Suggestion IA')}</div>
                <div className="text-xl font-bold text-blue-800 mt-1">G{suggestion.residualGravity} × V{suggestion.residualLikelihood} = {suggestedLevel}</div>
              </div>
            </div>
            {suggestion.gravityReason ? (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <div className="text-[11px] font-semibold text-slate-500 uppercase mb-1">{t('workshop5ResidualCards.aiGravityReason', 'Pourquoi cette gravité')} (G{suggestion.residualGravity})</div>
                <p className="text-xs text-slate-700 whitespace-pre-wrap">{suggestion.gravityReason}</p>
              </div>
            ) : null}
            {suggestion.likelihoodReason ? (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <div className="text-[11px] font-semibold text-slate-500 uppercase mb-1">{t('workshop5ResidualCards.aiLikelihoodReason', 'Pourquoi cette vraisemblance')} (V{suggestion.residualLikelihood})</div>
                <p className="text-xs text-slate-700 whitespace-pre-wrap">{suggestion.likelihoodReason}</p>
              </div>
            ) : null}
            {suggestion.reasoning ? (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <div className="text-[11px] font-semibold text-slate-500 uppercase mb-1">{t('workshop5ResidualCards.aiReasoning', 'Justification')}</div>
                <p className="text-xs text-slate-700 whitespace-pre-wrap">{suggestion.reasoning}</p>
              </div>
            ) : null}
          </div>
          <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 bg-white border border-slate-200 rounded-lg hover:bg-slate-50">{t('common.cancel', 'Annuler')}</button>
            <button onClick={onApply} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg">{t('workshop5ResidualCards.aiApply', 'Appliquer')}</button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
};

const ResidualRiskCards = ({ attackPathScenarios, riskScenarioIndexMap, strategicScenarios, riskScenarios, riskLevelsCount, riskLevels }) => {
  const { t, i18n } = useTranslation();
  const analysisId = useAnalysisStore((s) => s.currentAnalysis?.id);
  const [aiLoadingId, setAiLoadingId] = useState(null);
  const [aiModal, setAiModal] = useState(null);

  const handleSuggestResidual = async (ap, ss, residual, measures, initialG, initialL, shortRef) => {
    if (!ap?.id || aiLoadingId) return;
    setAiLoadingId(ap.id);
    try {
      const payload = {
        analysisId,
        riskLevelsCount,
        language: i18n.language?.startsWith('en') ? 'en' : 'fr',
        riskScenario: {
          shortRef,
          attackPathName: ap.name || ap.id,
          initialGravity: initialG,
          initialLikelihood: initialL,
          initialLevel: initialG * initialL,
          fearedEvents: (ss?.dreadedEvents || []).map((de) => de.name || de.referenceCode).filter(Boolean),
          description: residual?.description || '',
          vulnerabilities: residual?.vulnerabilities || '',
          aggravatingFactors: residual?.aggravatingFactors || '',
          monitoring: residual?.monitoring || '',
          measures: (measures || []).map((m) => m.name || m.title || m.measureName || '').filter(Boolean),
        },
      };
      const response = await api.post('/ai/suggest-residual-risk', payload);
      if (response?.success && response?.data) {
        setAiModal({ apId: ap.id, ssId: ss?.id, suggestion: response.data, initialG, initialL, shortRef, attackPathName: ap.name });
      } else {
        throw new Error(response?.message || 'Invalid AI response');
      }
    } catch (error) {
      handleAIError(error, { language: i18n.language?.startsWith('en') ? 'en' : 'fr' });
    } finally {
      setAiLoadingId(null);
    }
  };

  const handleApplySuggestion = () => {
    if (!aiModal) return;
    // Single store write: read fresh state (not the render closure) so both
    // axes land together — two sequential updateField calls lost the first one.
    const store = useAtelier5Store.getState();
    const freshSs = store.synthesisData?.strategicScenarios?.find((s) => s.id === aiModal.ssId);
    const current = freshSs?.attackPathResidualRisks?.[aiModal.apId] || {};
    store.addAttackPathResidualRisk(aiModal.ssId, aiModal.apId, {
      ...current,
      residualGravity: aiModal.suggestion.residualGravity,
      residualLikelihood: aiModal.suggestion.residualLikelihood,
      suggestedByAI: true,
      suggestedAt: new Date().toISOString(),
    });
    setAiModal(null);
  };

  const updateField = (apId, field, value, extra) => {
    const store = useAtelier5Store.getState();
    for (const ss of (strategicScenarios || [])) {
      if (ss.attackPaths?.some(ap => (typeof ap === 'string' ? ap : ap.id) === apId)) {
        const current = ss.attackPathResidualRisks?.[apId] || {};
        store.addAttackPathResidualRisk(ss.id, apId, { ...current, [field]: value, ...(extra || {}) });
        break;
      }
    }
  };

  const ssMap = {};
  for (const ss of (strategicScenarios || [])) {
    for (const ap of (ss.attackPaths || [])) {
      const id = typeof ap === 'string' ? ap : ap.id;
      if (id) ssMap[id] = ss;
    }
  }

  const levelColor = (level) => {
    const lv = (riskLevels || []).find(l => level >= l.minScore && level <= l.maxScore);
    if (!lv) return level >= 12 ? 'text-red-600' : level >= 8 ? 'text-orange-600' : level >= 4 ? 'text-yellow-600' : 'text-green-600';
    return LEVEL_COLOR_TEXT[lv.color] || 'text-green-600';
  };

  const levelBg = (level) => {
    const lv = (riskLevels || []).find(l => level >= l.minScore && level <= l.maxScore);
    if (!lv) return level >= 12 ? 'bg-red-100' : level >= 8 ? 'bg-orange-100' : level >= 4 ? 'bg-yellow-100' : 'bg-green-100';
    return LEVEL_COLOR_BG[lv.color] || 'bg-green-100';
  };

  return (
    <div className="space-y-4">
      <h3 className="text-base font-bold text-gray-900">
        {t('workshop5ResidualCards.title')}
      </h3>
      <div className="grid grid-cols-1 gap-4">
        {attackPathScenarios.map(ap => {
          const ref = riskScenarioIndexMap[ap.id];
          const shortRef = ref ? `RR${ref.shortRef.replace('R', '').padStart(2, '0')}` : ap.referenceCode || ap.id;
          const ss = ssMap[ap.id];
          const rs = riskScenarios.find(r => r.attackPathId === ap.id);
          const residual = rs?.residualRisk || {};

          const initialG = ap.gravity;
          const initialL = ap.likelihood;
          const initialLevel = initialG * initialL;
          const residualG = Number(residual.residualGravity) || initialG;
          const residualL = Number(residual.residualLikelihood) || initialL;
          const residualLevel = residualG * residualL;

          const changeG = (delta) => {
            const next = Math.max(1, Math.min(riskLevelsCount, residualG + delta));
            updateField(ap.id, 'residualGravity', next, { suggestedByAI: false });
          };
          const changeL = (delta) => {
            const next = Math.max(1, Math.min(4, residualL + delta));
            updateField(ap.id, 'residualLikelihood', next, { suggestedByAI: false });
          };

          const measures = [
            ...(ss?.dreadedEventMeasures || []),
            ...(ss?.ecosystemMeasures || []),
            ...(ss?.customMeasures || []),
          ].filter(m => m.retained !== false).filter(m => {
            const ids = [];
            if (m.attackPaths?.length) m.attackPaths.forEach(ap => ids.push(typeof ap === 'string' ? ap : ap));
            if (m.attackPathId) ids.push(m.attackPathId);
            return ids.length === 0 || ids.some(id => id === ap.id);
          });

          return (
            <div key={ap.id} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="px-4 py-2 bg-slate-800 text-white text-sm font-bold flex items-center justify-between">
                <span className="flex items-center gap-2">
                  {shortRef} — {ap.name}
                  {ap.preliminary && (
                    <span className="text-[10px] font-semibold uppercase tracking-wide bg-purple-500 text-white rounded px-1.5 py-0.5">
                      {t('workshop5.preliminary.shortLabel', 'Étude préliminaire')}
                    </span>
                  )}
                </span>
                <span className="flex items-center gap-2">
                  <span className={`text-sm font-bold px-2 py-0.5 rounded ${levelBg(initialLevel)} ${levelColor(initialLevel)}`}>
                    {t('workshop5ResidualCards.initialLevel')} {initialLevel}
                  </span>
                  <AiAssistantButton
                    onClick={() => handleSuggestResidual(ap, ss, residual, measures, initialG, initialL, shortRef)}
                    isLoading={aiLoadingId === ap.id}
                    title={t('workshop5ResidualCards.aiTitle', 'Assistant ACG — suggérer le risque résiduel')}
                    className="!bg-white/10 !text-white !border-white/20 hover:!bg-white/20"
                  />
                </span>
              </div>

              <div className="p-3">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-sm font-semibold text-slate-700">{t('workshop5ResidualCards.events')}</span>
                      {ss?.dreadedEvents?.length > 0 ? (
                        ss.dreadedEvents.map((de, i) => (
                          <span key={de.id || i} className="inline-flex items-center px-2 py-0.5 rounded text-sm font-medium bg-red-100 text-red-800">
                            {de.name || de.referenceCode || `Événement ${i + 1}`}
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-slate-500 italic">{t('workshop5ResidualCards.noEvents')}</span>
                      )}
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-slate-700 block mb-1">{t('workshop5ResidualCards.descriptionAnalysis')}</span>
                      <div className="space-y-1">
                        <div>
                          <span className="text-sm text-slate-600"><span className="mr-1.5">•</span>{t('workshop5ResidualCards.fieldDescription')}</span>
                          <EditableField value={residual.description} onSave={v => updateField(ap.id, 'description', v)} placeholder={t('workshop5ResidualCards.placeholderDescription')} multiline />
                        </div>
                        <div>
                          <span className="text-sm text-slate-600"><span className="mr-1.5">•</span>{t('workshop5ResidualCards.fieldVulnerabilities')}</span>
                          <EditableField value={residual.vulnerabilities} onSave={v => updateField(ap.id, 'vulnerabilities', v)} placeholder={t('workshop5ResidualCards.placeholderVulnerabilities')} multiline />
                        </div>
                        <div>
                          <span className="text-sm text-slate-600"><span className="mr-1.5">•</span>{t('workshop5ResidualCards.fieldAggravating')}</span>
                          <EditableField value={residual.aggravatingFactors} onSave={v => updateField(ap.id, 'aggravatingFactors', v)} placeholder={t('workshop5ResidualCards.placeholderAggravating')} multiline />
                        </div>
                      </div>
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-slate-700 block mb-0.5">{t('workshop5ResidualCards.management')}</span>
                      <EditableField value={residual.monitoring} onSave={v => updateField(ap.id, 'monitoring', v)} placeholder={t('workshop5ResidualCards.placeholderMonitoring')} multiline />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div className="bg-slate-100 rounded-lg p-2.5">
                        <span className="text-sm font-semibold text-slate-600 uppercase block mb-1">{t('workshop5ResidualCards.initialRisk')}</span>
                        <div className="space-y-0.5 text-sm">
                          <div className="flex justify-between"><span className="text-slate-500">{t('workshop5ResidualCards.gravity')}</span><span className="font-bold text-slate-800">{initialG}</span></div>
                          <div className="flex justify-between"><span className="text-slate-500">{t('workshop5ResidualCards.likelihood')}</span><span className="font-bold text-slate-800">{initialL}</span></div>
                          <div className="border-t border-slate-300 pt-0.5 mt-0.5 flex justify-between">
<span className="text-slate-500">{t('workshop5ResidualCards.level')}</span>
                            <span className={`font-bold ${levelColor(initialLevel)}`}>{initialLevel}</span>
                          </div>
                        </div>
                      </div>
                      <div className="bg-blue-100 rounded-lg p-2.5">
                        <span className="text-sm font-semibold text-slate-600 uppercase block mb-1">
                          {t('workshop5ResidualCards.residualRisk')}
                          {residual.suggestedByAI ? (
                            <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium normal-case bg-purple-100 text-purple-700 border border-purple-200 align-middle">
                              {t('workshop5ResidualCards.aiSuggestedBadge', 'Suggéré par l’Assistant ACG')}
                            </span>
                          ) : null}
                        </span>
                        <div className="space-y-0.5 text-sm">
                          <div className="flex justify-between items-center">
                            <span className="text-slate-500">{t('workshop5ResidualCards.gravity')}</span>
                            <div className="flex items-center gap-1">
                              <span className="font-bold text-slate-800 w-5 text-center">{residualG}</span>
                              <div className="flex flex-col items-center gap-0.5">
                                <button onClick={() => changeG(1)} className="text-[8px] text-slate-500 hover:text-slate-900 leading-none p-0 h-2.5">▲</button>
                                <button onClick={() => changeG(-1)} className="text-[8px] text-slate-500 hover:text-slate-900 leading-none p-0 h-2.5">▼</button>
                              </div>
                            </div>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-slate-500">{t('workshop5ResidualCards.likelihood')}</span>
                            <div className="flex items-center gap-1">
                              <span className="font-bold text-slate-800 w-5 text-center">{residualL}</span>
                              <div className="flex flex-col items-center gap-0.5">
                                <button onClick={() => changeL(1)} className="text-[8px] text-slate-500 hover:text-slate-900 leading-none p-0 h-2.5">▲</button>
                                <button onClick={() => changeL(-1)} className="text-[8px] text-slate-500 hover:text-slate-900 leading-none p-0 h-2.5">▼</button>
                              </div>
                            </div>
                          </div>
                          <div className="border-t border-blue-300 pt-0.5 mt-0.5 flex justify-between">
<span className="text-slate-500">{t('workshop5ResidualCards.level')}</span>
                            <span className={`font-bold ${levelColor(residualLevel)}`}>{residualLevel}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-slate-700 block mb-1">{t('workshop5ResidualCards.treatmentMeasures')}</span>
                      {measures.length > 0 ? (
                        <div className="space-y-0.5 max-h-[100px] overflow-y-auto">
                          {measures.map(m => {
                            const statusColors = {
                              planned: 'bg-blue-100 text-blue-700',
                              in_progress: 'bg-amber-100 text-amber-700',
                              implemented: 'bg-green-100 text-green-700',
                              not_planned: 'bg-orange-100 text-orange-700',
                              abandoned: 'bg-red-100 text-red-700',
                            };
                            const statusClass = statusColors[m.implementation] || 'bg-slate-100 text-slate-600';
                            return (
                              <div key={m.id} className="flex items-start gap-1.5 text-sm text-slate-700">
                                <span className="text-slate-400 shrink-0">•</span>
                                <span className="flex-1 min-w-0 truncate">{m.name || m.title || m.measureName || '—'}</span>
                                {m.implementation ? (
                                  <span className={`shrink-0 inline-block text-[10px] font-medium px-1.5 py-0.5 rounded ${statusClass}`}>
                                    {t('workshop5RS.statusOptions.' + m.implementation, m.implementation)}
                                  </span>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-sm text-slate-500 italic">{t('workshop5ResidualCards.noMeasures')}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <ResidualRiskAiModal data={aiModal} onClose={() => setAiModal(null)} onApply={handleApplySuggestion} />
    </div>
  );
};

const ImplementationScheduleTable = ({ strategicScenarios, riskScenarioIndexMap }) => {
  const { t } = useTranslation();

  const allMeasures = useMemo(() => {
    const seen = new Map();
    for (const ss of (strategicScenarios || [])) {
      const scenarioAttackPathIds = new Set();
      (ss.attackPaths || []).forEach((ap) => {
        const id = typeof ap === 'string' ? ap : ap?.id;
        if (id) scenarioAttackPathIds.add(id);
      });
      const resolveRef = (m) => {
        const ids = new Set();
        if (m.attackPathId) ids.add(m.attackPathId);
        if (m.attackPaths?.length) m.attackPaths.forEach(a => ids.add(typeof a === 'string' ? a : (a.id || a)));
        return [...ids].map(id => riskScenarioIndexMap[id]?.shortRef).filter(Boolean).join(', ') || null;
      };
      const pushIfNew = (arr, source) => {
        for (const m of (arr || [])) {
          if (!seen.has(m.id) && m.retained !== false) {
            const enriched = { ...m, source };
            if (source === 'dreadedEvent' && !m.attackPathId && !m.attackPaths?.length && scenarioAttackPathIds.size) {
              enriched.attackPaths = [...scenarioAttackPathIds];
            }
            enriched.displayAttackPathRef = resolveRef(enriched);
            seen.set(m.id, enriched);
          }
        }
      };
      pushIfNew(ss.customMeasures, 'custom');
      pushIfNew(ss.dreadedEventMeasures, 'dreadedEvent');
      const ecoMeasures = (ss.ecosystemMeasures || []).filter(m => {
        const ids = [];
        if (m.attackPaths?.length) m.attackPaths.forEach(a => ids.push(typeof a === 'string' ? a : (a.id || a)));
        if (m.attackPathId) ids.push(m.attackPathId);
        return ids.some(id => scenarioAttackPathIds.has(id));
      });
      pushIfNew(ecoMeasures, 'ecosystem');
    }
    return [...seen.values()];
  }, [strategicScenarios, riskScenarioIndexMap]);

  const grouped = useMemo(() => {
    const msMonth = 30.44 * 24 * 60 * 60 * 1000;
    const now = new Date();
    const quarterKey = (deadline) => {
      if (!deadline) return 'none';
      const d = new Date(deadline + 'T00:00:00');
      if (isNaN(d.getTime())) return 'none';
      const months = (d - now) / msMonth;
      if (months <= 3) return 't0';
      if (months <= 6) return 't3';
      if (months <= 9) return 't6';
      if (months <= 12) return 't9';
      return 't12';
    };
    const g = {};
    for (const m of allMeasures) {
      const q = quarterKey(m.deadline);
      if (!g[q]) g[q] = [];
      g[q].push(m);
    }
    return g;
  }, [allMeasures]);

  const qLabel = {
    t0: 'T+0 (0 \u00e0 3 mois)',
    t3: 'T+3 (3 \u00e0 6 mois)',
    t6: 'T+6 (6 \u00e0 9 mois)',
    t9: 'T+9 (9 \u00e0 12 mois)',
    t12: 'T+12 (12 mois et +)',
    none: 'Non planifi\u00e9',
  };

  const qColor = {
    t0: 'bg-indigo-900 text-white',
    t3: 'bg-blue-700 text-white',
    t6: 'bg-blue-500 text-white',
    t9: 'bg-blue-400 text-white',
    t12: 'bg-blue-300 text-white',
    none: 'bg-slate-500 text-white',
  };

  const qOrder = ['t0', 't3', 't6', 't9', 't12', 'none'];
  const sortedQuarters = qOrder.filter(q => (grouped[q]?.length || 0) > 0);

  if (!allMeasures.length) return null;

  const statusColors = {
    planned: 'bg-blue-100 text-blue-700',
    in_progress: 'bg-amber-100 text-amber-700',
    implemented: 'bg-green-100 text-green-700',
    not_planned: 'bg-orange-100 text-orange-700',
    abandoned: 'bg-red-100 text-red-700',
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
        <h3 className="text-sm font-semibold text-slate-700">
          {t('workshop5ResidualCards.timelineTitle', '\u00c9ch\u00e9ancier de mise en \u0153uvre')}
        </h3>
        <p className="text-xs text-slate-500 mt-0.5">
          {t('workshop5ResidualCards.timelineDesc')}
        </p>
      </div>
      <div className="overflow-x-auto p-1">
        <table className="w-full text-sm border border-slate-200">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left px-3 py-2.5 font-semibold text-slate-700 text-xs">{t('workshop5RS.measure', 'Mesure')}</th>
              <th className="text-left px-3 py-2.5 font-semibold text-slate-700 text-xs">{t('workshop5RS.associatedScenario', 'Sc\u00e9nario')}</th>
              <th className="text-left px-3 py-2.5 font-semibold text-slate-700 text-xs">{t('workshop5RS.responsible', 'Responsable')}</th>
              <th className="text-left px-3 py-2.5 font-semibold text-slate-700 text-xs">{t('workshop5RS.deadline', '\u00c9ch\u00e9ance')}</th>
              <th className="text-center px-3 py-2.5 font-semibold text-slate-700 text-xs">{t('workshop5RS.estimatedEffort', 'Charge (j/h)')}</th>
              <th className="text-center px-3 py-2.5 font-semibold text-slate-700 text-xs">{t('workshop5RS.status', 'Statut')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sortedQuarters.map(q => (
              <React.Fragment key={q}>
                <tr>
                  <td colSpan={6} className={`px-4 py-0.5 text-sm font-bold tracking-wide text-center ${qColor[q] || 'bg-slate-600 text-white'}`}>
                    {qLabel[q]} ({t('workshop5RS.measuresCount', { count: grouped[q].length })})
                  </td>
                </tr>
                {grouped[q].map(m => (
                  <tr key={m.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-3 py-2.5">
                      <div className="text-xs font-medium text-slate-800">{m.title || m.name || m.measureName || m.description || '\u2014'}</div>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-600">{m.displayAttackPathRef || '\u2014'}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-600">{m.responsiblePerson || m.responsibleParty || '\u2014'}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-600 font-mono">{m.deadline || '\u2014'}</td>
                    <td className="px-3 py-2.5 text-xs text-center text-slate-600 font-mono">{m.estimatedEffort || m.effort || '\u2014'}</td>
                    <td className="px-3 py-2.5 text-center">
                      {(() => {
                        const s = m.implementation || '';
                        return s ? (
                          <span className={`inline-block text-[10px] font-medium px-2 py-0.5 rounded ${statusColors[s] || 'bg-slate-100 text-slate-600'}`}>
                            {t('workshop5RS.statusOptions.' + s, s)}
                          </span>
                        ) : <span className="text-xs text-slate-400">\u2014</span>;
                      })()}
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ImplementationTimeline = ({ strategicScenarios }) => {
  const { t } = useTranslation();
  const [api, setApi] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const containerRef = useRef(null);
  const currentAnalysis = useAnalysisStore((s) => s.currentAnalysis);

  const t0 = useMemo(() => {
    const d = currentAnalysis?.createdAt ? parseISO(currentAnalysis.createdAt) : new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, [currentAnalysis]);

  useEffect(() => {
    const onFsChange = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else if (containerRef.current) {
      containerRef.current.requestFullscreen();
    }
  };

  const tasks = useMemo(() => {
    const pMap = { planned: 10, in_progress: 50, implemented: 100, not_planned: 0, abandoned: 0 };
    const seen = new Map();
    for (const ss of (strategicScenarios || [])) {
      for (const arr of [ss.customMeasures, ss.dreadedEventMeasures, ss.ecosystemMeasures]) {
        for (const m of (arr || [])) {
          if (!seen.has(m.id) && m.retained !== false && m.deadline) {
            const endD = parseISO(m.deadline);
            if (isNaN(endD.getTime())) continue;
            const effort = Number(m.estimatedEffort) || 0;
            const startD = new Date(endD.getTime() - effort * 86400000);
            seen.set(m.id, {
              id: m.id,
              text: (m.name || m.title || m.measureName || '—').substring(0, 40),
              start: startD,
              end: endD,
              effort,
              status: t('workshop5RS.statusOptions.' + (m.implementation || ''), m.implementation || ''),
              progress: pMap[m.implementation] || 0,
            });
          }
        }
      }
    }
    return [...seen.values()];
  }, [strategicScenarios]);

  const range = useMemo(() => {
    const t6 = new Date(t0); t6.setMonth(t6.getMonth() + 6);
    const t6plus = new Date(t6); t6plus.setDate(t6plus.getDate() + 14);
    if (!tasks.length) return { start: t0, end: t6plus };
    let min = Infinity, max = -Infinity;
    tasks.forEach(t => {
      const s = t.start.getTime(), e = t.end.getTime();
      if (s < min) min = s;
      if (e > max) max = e;
    });
    const oneWeek = 7 * 86400000;
    return {
      start: new Date(Math.min(min - oneWeek, t0.getTime() - oneWeek)),
      end: new Date(Math.max(max + 14 * 86400000, t6plus.getTime())),
    };
  }, [tasks, t0]);

  const columns = [
    { id: 'text',   header: t('workshop5ResidualCards.measure'), flexgrow: 2, sort: true },
    { id: 'start',  header: t('workshop5ResidualCards.start'),   width: 100, align: 'center' },
    { id: 'end',    header: t('workshop5ResidualCards.end'),     width: 100, align: 'center' },
    { id: 'effort', header: t('workshop5ResidualCards.effort'),  width: 90,  align: 'center' },
    { id: 'status', header: t('workshop5ResidualCards.statut'),  width: 90,  align: 'center' },
  ];

  const scales = [
    { unit: 'month', step: 1, format: '%M %Y' },
  ];

  const markers = useMemo(() => {
    const t3 = new Date(t0); t3.setMonth(t3.getMonth() + 3);
    const t6 = new Date(t0); t6.setMonth(t6.getMonth() + 6);
    return [
      { start: new Date(t0), text: 'T+0', css: 'wx-marker' },
      { start: t3, text: 'T+3', css: 'wx-marker' },
      { start: t6, text: 'T+6', css: 'wx-marker' },
    ];
  }, [t0]);

  useEffect(() => {
    if (!api) return;
    api.on('update-task', ({ id, task, inProgress }) => {
      if (inProgress) return;
      const endStr = task.end ? task.end.toISOString().split('T')[0] : '';
      if (endStr) {
        const store = useAtelier5Store.getState();
        strategicScenarios.forEach((ss) =>
          store.updateMeasureField(ss.id, id, 'deadline', endStr)
        );
      }
    });
  }, [api, strategicScenarios]);

  if (!tasks.length) return null;

  const h = Math.max(200, tasks.length * 42 + 60);

  return (
    <div ref={containerRef} className={`bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex flex-col ${fullscreen ? 'fixed inset-0 z-50 rounded-none' : ''}`}>
      <div className="flex items-center justify-between mb-2 shrink-0">
        <h3 className="text-base font-bold text-gray-900">{t('workshop5ResidualCards.timelineTitle')}</h3>
        <button onClick={toggleFullscreen} className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition-colors" title={fullscreen ? t('workshop5ResidualCards.exitFullscreen') : t('workshop5ResidualCards.fullscreen')}>
          {fullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
        </button>
      </div>
      <style>{`.wx-willow-theme .wx-marker { background:rgba(239,68,68,0.08); border-left:2px solid rgb(239,68,68); }`}</style>
      <div className={fullscreen ? 'flex-1 min-h-0' : ''} style={fullscreen ? {} : { height: h }}>
        <Willow>
          <Gantt tasks={tasks} columns={columns} scales={scales} start={range.start} end={range.end} markers={markers} init={setApi} />
        </Willow>
      </div>
    </div>
  );
};

const useTreatmentOptions = () => {
  const { t } = useTranslation();
  return [
    { value: 'reduce', label: t('workshop5.treatmentTable.options.reduce', 'Reduce'), color: 'bg-blue-100 text-blue-800 border-blue-300' },
    { value: 'accept', label: t('workshop5.treatmentTable.options.accept', 'Accept'), color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
    { value: 'transfer', label: t('workshop5.treatmentTable.options.transfer', 'Transfer'), color: 'bg-purple-100 text-purple-800 border-purple-300' },
    { value: 'avoid', label: t('workshop5.treatmentTable.options.avoid', 'Avoid'), color: 'bg-red-100 text-red-800 border-red-300' },
  ];
};

const RiskScenarioTreatmentTable = ({ strategicScenarios, attackPathScenarios, riskScenarioIndexMap, riskLevels }) => {
  const { t } = useTranslation();
  const TREATMENT_OPTIONS = useTreatmentOptions();
  const responsiblePersonsList = useAtelier1Store((s) => s.contextData?.responsiblePersonsList) || [];

  const rows = useMemo(() => {
    return attackPathScenarios.map(ap => {
      const ss = strategicScenarios.find(s =>
        s.attackPaths?.some(a => (typeof a === 'string' ? a : a.id) === ap.id)
      );
      if (!ss) return null;

      const treatment = ss.attackPathTreatments?.[ap.id] || {};
      const residual = ss.attackPathResidualRisks?.[ap.id] || {};

      const measures = [
        ...(ss.customMeasures || []),
        ...(ss.dreadedEventMeasures || []),
        ...(ss.ecosystemMeasures || []),
      ].filter(m => m.retained !== false).filter(m => {
        const ids = [];
        if (m.attackPaths?.length) m.attackPaths.forEach(a => ids.push(typeof a === 'string' ? a : a));
        if (m.attackPathId) ids.push(m.attackPathId);
        return ids.length === 0 || ids.some(id => id === ap.id);
      });

      return {
        attackPathId: ap.id,
        shortRef: riskScenarioIndexMap[ap.id]?.shortRef || '',
        name: ap.name,
        initialGravity: ap.gravity,
        initialLikelihood: ap.likelihood,
        initialRisk: ap.gravity * ap.likelihood,
        option: treatment.option || '',
        justification: treatment.justification || '',
        responsiblePerson: treatment.responsiblePerson || '',
        residualGravity: Number(residual.residualGravity) || ap.gravity,
        residualLikelihood: Number(residual.residualLikelihood) || ap.likelihood,
        measuresList: measures.map(m => ({ id: m.id, scenarioId: ss.id, name: m.name || m.title || '', budget: Number(m.budget) || 0, implementation: m.implementation })),
        measuresCount: measures.length,
        strategicScenarioId: ss.id,
      };
    }).filter(Boolean);
  }, [strategicScenarios, attackPathScenarios, riskScenarioIndexMap]);

  const updateField = (strategicScenarioId, attackPathId, field, value) => {
    const store = useAtelier5Store.getState();
    const ss = strategicScenarios.find(s => s.id === strategicScenarioId);
    const current = ss?.attackPathTreatments?.[attackPathId] || {};
    store.addAttackPathTreatment(strategicScenarioId, attackPathId, { ...current, [field]: value });
  };

  const updateMeasureBudget = (scenarioId, measureId, value) => {
    const store = useAtelier5Store.getState();
    store.updateMeasureField(scenarioId, measureId, 'budget', value);
  };

  const totalBudget = useMemo(() => {
    const seen = new Set();
    let total = 0;
    const excluded = ['not_planned', 'abandoned', 'implemented'];
    for (const row of rows) {
      for (const m of row.measuresList) {
        if (m.id && !seen.has(m.id) && !excluded.includes(m.implementation)) {
          seen.add(m.id);
          total += m.budget;
        }
      }
    }
    return total;
  }, [rows]);

  if (!rows.length) return null;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">{t('workshop5.treatmentTable.title', 'Risk Scenario Treatment')}</h3>
        <span className="text-xs text-slate-500">{rows.length} {t('workshop5.treatmentTable.riskScenarios', 'risk scenarios')}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left px-3 py-2.5 font-semibold text-slate-700 text-xs">{t('workshop5.treatmentTable.ref', 'Ref')}</th>
              <th className="text-left px-3 py-2.5 font-semibold text-slate-700 text-xs w-32">{t('workshop5.treatmentTable.attackPath', 'Attack Path')}</th>
              <th className="text-left px-3 py-2.5 font-semibold text-slate-700 text-xs">{t('workshop5.treatmentTable.initialRisk', 'Initial Risk')}</th>
              <th className="text-left px-3 py-2.5 font-semibold text-slate-700 text-xs">{t('workshop5.treatmentTable.treatment', 'Treatment')}</th>
              <th className="text-left px-3 py-2.5 font-semibold text-slate-700 text-xs w-[280px]">{t('workshop5.treatmentTable.justification', 'Justification')}</th>
              <th className="text-left px-3 py-2.5 font-semibold text-slate-700 text-xs">{t('workshop5.treatmentTable.responsible', 'Responsible')}</th>
              <th className="text-left px-3 py-2.5 font-semibold text-slate-700 text-xs">{t('workshop5.treatmentTable.residualRisk', 'Residual Risk')}</th>
              <th className="text-left px-3 py-2.5 font-semibold text-slate-700 text-xs w-[420px]">{t('workshop5.treatmentTable.measures', 'Measures')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map(row => {
              const resLevel = row.residualGravity * row.residualLikelihood;
              const resColor = getRiskLevelColor(resLevel, riskLevels);
              const initColor = getRiskLevelColor(row.initialRisk, riskLevels);
              return (
                <tr key={row.attackPathId} className="hover:bg-slate-50 transition-colors">
                  <td className="px-3 py-2.5 text-xs font-mono font-bold text-slate-700">{row.shortRef}</td>
                  <td className="px-3 py-2.5 text-xs text-slate-600">{row.name}</td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${initColor.bg} ${initColor.text}`}>
                      {row.initialRisk}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <select
                      value={row.option}
                      onChange={e => updateField(row.strategicScenarioId, row.attackPathId, 'option', e.target.value)}
                      className="text-xs border border-slate-200 rounded px-2 py-1 pr-6 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    >
                      <option value="">—</option>
                      {TREATMENT_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2.5 w-[280px] max-w-[280px]">
                    <EditableField
                      value={row.justification}
                      onSave={v => updateField(row.strategicScenarioId, row.attackPathId, 'justification', v)}
                      placeholder={t('workshop5.treatmentTable.placeholderJustification', 'Add justification...')}
                      multiline
                    />
                  </td>
                  <td className="px-3 py-2.5">
                    <select
                      value={row.responsiblePerson}
                      onChange={e => updateField(row.strategicScenarioId, row.attackPathId, 'responsiblePerson', e.target.value)}
                      className="text-xs border border-slate-200 rounded px-2 py-1 pr-6 focus:outline-none focus:ring-1 focus:ring-blue-400 max-w-[160px]"
                    >
                      <option value="">—</option>
                      {responsiblePersonsList.map((p) => (
                        <option key={p.id} value={p.name}>{p.name}{p.role ? ` (${p.role})` : ''}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${resColor.bg} ${resColor.text}`}>
                      {resLevel}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-slate-600 w-[420px] max-w-[420px]">{row.measuresList.map((m, i) => <div key={i} className="flex justify-between items-start gap-2 mb-0.5"><span className="whitespace-normal break-words min-w-0 flex-1">- {m.name}</span><span className="flex items-center gap-0.5 shrink-0"><input type="number" min="0" value={m.budget || 0} onChange={e => updateMeasureBudget(m.scenarioId, m.id, e.target.value)} className="w-14 text-xs border border-slate-200 rounded px-1 py-0.5 text-slate-700 text-right" /><span className="text-[10px] text-slate-400 w-3.5">€</span></span></div>)}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-slate-50 border-t-2 border-slate-200">
              <td colSpan={7} className="px-3 py-2 text-xs font-semibold text-slate-700 text-right">{t('workshop5.treatmentTable.totalBudget', 'Total budget')}</td>
              <td className="px-3 py-2 text-xs font-bold text-slate-800">{(totalBudget || 0)} €</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

const DreadedEventCoverage = ({ strategicScenarios, operationalScenarios, riskScenarioIndexMap }) => {
  const { t } = useTranslation();
  const dreadedEvents = useAtelier1Store((s) => s.dreadedEvents);
  const businessValues = useAtelier1Store((s) => s.businessValues);
  const isA1Loaded = useAtelier1Store((s) => s.isDataLoaded);
  const analysisId = useAnalysisStore((s) => s.currentAnalysis?.id);

  useEffect(() => {
    if (!isA1Loaded && analysisId) {
      useAtelier1Store.getState().loadData(analysisId);
    }
  }, [isA1Loaded, analysisId]);

  const bvNameById = useMemo(() => {
    const map = {};
    for (const bv of (businessValues || [])) map[bv.id] = bv.name || null;
    return map;
  }, [businessValues]);

  const { rows } = useMemo(
    () => buildCoverageRegistry({
      dreadedEvents,
      strategicScenarios,
      operationalScenarios,
      riskScenarioIndexMap,
      bvNameById,
    }),
    [dreadedEvents, strategicScenarios, operationalScenarios, riskScenarioIndexMap, bvNameById]
  );

  const [justifications, setJustifications] = useState({});
  const initialized = useRef(false);
  useEffect(() => {
    if (!initialized.current && dreadedEvents?.length) {
      const initial = {};
      for (const de of dreadedEvents) {
        if (de.coverageJustification) initial[de.id || de.name] = de.coverageJustification;
      }
      if (Object.keys(initial).length) setJustifications(initial);
      initialized.current = true;
    }
  }, [dreadedEvents]);

  const handleSaveJustification = (key, text) => {
    setJustifications(prev => ({ ...prev, [key]: text }));
    const updated = (dreadedEvents || []).map(de =>
      (de.id === key || de.name === key) ? { ...de, coverageJustification: text } : de
    );
    useAtelier1Store.getState().saveDreadedEvents(updated).catch(err => {
      console.error('[Atelier5] save justification failed:', err);
    });
  };

  const SeverityBadge = ({ severity }) => {
    if (!severity) return <span className="text-slate-300 text-[11px]">—</span>;
    const styles = {
      minor: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      moderate: 'bg-amber-50 text-amber-700 border-amber-200',
      major: 'bg-orange-50 text-orange-800 border-orange-200',
      critical: 'bg-red-50 text-red-700 border-red-200',
      catastrophic: 'bg-slate-900 text-slate-50 border-slate-700',
    };
    const cls = styles[severity] || 'bg-slate-100 text-slate-700 border-slate-200';
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border whitespace-nowrap ${cls}`}>
        {severity === 'critical' && <span className="w-1.5 h-1.5 rounded-full bg-red-500 mr-1" />}
        {getTranslatedSeverityLabel(severity)}
      </span>
    );
  };

  const JustificationEditor = ({ value, onSave, placeholder }) => {
    const [editing, setEditing] = useState(false);
    const [val, setVal] = useState(value || '');
    useEffect(() => { setVal(value || ''); }, [value]);
    if (!editing) {
      return (
        <span
          className="cursor-pointer hover:bg-slate-100 px-1.5 py-0.5 rounded -mx-1.5 block text-xs text-slate-700 min-h-[20px] whitespace-pre-wrap"
          onClick={() => setEditing(true)}
        >
          {value || <span className="text-slate-300 italic">{placeholder}</span>}
        </span>
      );
    }
    const commit = () => { onSave(val); setEditing(false); };
    return (
      <div className="flex items-start gap-1">
        <textarea
          autoFocus
          value={val}
          onChange={e => setVal(e.target.value)}
          onBlur={commit}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              commit();
            } else if (e.key === 'Escape') {
              setVal(value || '');
              setEditing(false);
            }
          }}
          className="flex-1 text-xs border border-blue-400 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 resize-none"
          rows={3}
          placeholder={placeholder}
        />
        <button
          type="button"
          onMouseDown={e => e.preventDefault()}
          onClick={commit}
          className="inline-flex items-center justify-center w-7 h-7 rounded bg-blue-600 hover:bg-blue-700 text-white shrink-0"
          title={t('workshop5Coverage.saveJustification', 'Enregistrer')}
        >
          <Check size={14} />
        </button>
      </div>
    );
  };

  if (!rows.length) return null;

  const uncovered = rows.filter(r => !r.isCovered);

  return (
    <div className="space-y-3">
      {uncovered.length > 0 && (
        <div className="bg-red-50 border-l-4 border-red-600 rounded-r-xl rounded-l-sm p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 shrink-0">
              <AlertTriangle size={18} className="text-red-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-bold text-red-900">
                {t('workshop5Coverage.exposureTitle', 'Événements redoutés sans scénario de risque')}
              </h3>
              <p className="text-xs text-red-700 mt-0.5">
                {t('workshop5Coverage.exposureDesc', 'Ces événements redoutés de l’atelier 1 n’apparaissent dans aucun scénario de risque des ateliers 3 et 4.')}
              </p>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {uncovered.map(r => (
                  <span key={r.key} className="inline-flex items-center gap-1.5 bg-white border border-red-200 rounded-full px-2.5 py-0.5 text-[11px] text-red-800 shadow-sm">
                    <span className="font-medium">{r.name}</span>
                    {r.bvName && (
                      <>
                        <span className="text-red-400">·</span>
                        <span className="text-red-600">{r.bvName}</span>
                      </>
                    )}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 bg-slate-900 text-white flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-slate-400">
              {t('workshop5Coverage.eyebrow', 'Registre de traçabilité')}
            </div>
            <h3 className="text-sm font-bold mt-0.5">
              {t('workshop5Coverage.title', 'Événements redoutés vs scénarios de risque')}
            </h3>
          </div>
          <div className="flex items-center gap-2 text-[10px] font-semibold">
            <span className="px-2 py-0.5 rounded bg-white/10 font-mono">{rows.length} ER</span>
            <span className={`px-2 py-0.5 rounded font-mono ${uncovered.length ? 'bg-red-600' : 'bg-green-600'}`}>
              {uncovered.length} {t('workshop5Coverage.gapsShort', 'non rattachés')}
            </span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs table-fixed">
            <colgroup>
              <col className="w-[32%]" />
              <col className="w-[12%]" />
              <col className="w-[18%]" />
              <col className="w-[14%]" />
              <col className="w-[24%]" />
            </colgroup>
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-3 py-2 font-semibold text-slate-700 text-[10px]">
                  {t('workshop5Coverage.colEvent', 'Événement redouté')}
                </th>
                <th className="text-left px-3 py-2 font-semibold text-slate-700 text-[10px]">
                  {t('workshop5Coverage.colSeverity', 'Gravité')}
                </th>
                <th className="text-left px-3 py-2 font-semibold text-slate-700 text-[10px]">
                  {t('workshop5Coverage.colBV', 'Valeur métier')}
                </th>
                <th className="text-left px-3 py-2 font-semibold text-slate-700 text-[10px]">
                  {t('workshop5Coverage.colScenarios', 'Scénarios de risque')}
                </th>
                <th className="text-left px-3 py-2 font-semibold text-slate-700 text-[10px]">
                  {t('workshop5Coverage.colJustification', 'Justification')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map(r => (
                <tr
                  key={r.key}
                  className={`hover:bg-slate-50 transition-colors ${r.isCovered ? '' : 'bg-red-50/70 border-l-4 border-l-red-600'}`}
                >
                  <td className="px-3 py-2.5 align-top">
                    <span className="text-slate-700 leading-snug line-clamp-2 block">{r.name}</span>
                  </td>
                  <td className="px-3 py-2.5 align-top">
                    <SeverityBadge severity={r.severity} />
                  </td>
                  <td className="px-3 py-2.5 align-top text-slate-600 leading-snug">
                    {r.bvName ? r.bvName : (
                      <span className="italic text-slate-400 text-[11px]">
                        {t('workshop5Coverage.bvMissing', 'valeur métier non chargée')}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 align-top">
                    {r.isCovered ? (
                      <div className="flex items-center gap-1 font-mono text-[11px] font-bold text-green-800 whitespace-nowrap overflow-hidden text-ellipsis">
                        <CheckCircle size={12} className="text-green-600 shrink-0" />
                        <span className="truncate">{r.scenarioRefs.join(' · ')}</span>
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700 border border-red-200 whitespace-nowrap">
                        <AlertTriangle size={10} /> {t('workshop5Coverage.none', 'Aucun')}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 align-top">
                    {r.isCovered ? (
                      <span className="text-slate-300">—</span>
                    ) : (
                      <JustificationEditor
                        value={justifications[r.key] || ''}
                        onSave={v => handleSaveJustification(r.key, v)}
                        placeholder={t('workshop5Coverage.placeholderJustification', 'Justifier l’absence de scénario de risque…')}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const RiskScenarioListView = ({ strategicScenarios, operationalScenarios }) => {
  const { t } = useTranslation();
  const currentAnalysis = useAnalysisStore((s) => s.currentAnalysis);
  const riskLevelsCount = currentAnalysis?.riskLevelsCount || 5;
  const riskLevelsConfig = useMemo(() => {
    const saved = currentAnalysis?.riskLevels;
    if (saved?.length) return saved;
    return generateDefaultLevels(riskLevelsCount);
  }, [currentAnalysis?.riskLevels, riskLevelsCount]);

  const attackPathScenarios = useMemo(() => {
    const results = [];
    for (const ss of (strategicScenarios || [])) {
      const severity = ss.severity;
      let gravity;
      if (riskLevelsCount === 3) {
        const m = { Faible: 1, Moyen: 2, Élevé: 3, Critique: 3, Catastrophique: 3,
                    minor: 1, moderate: 2, major: 3, critical: 3, catastrophic: 3 };
        gravity = m[severity] || 2;
      } else if (riskLevelsCount === 4) {
        const m = { Faible: 1, Moyen: 2, Élevé: 3, Critique: 4, Catastrophique: 4,
                    minor: 1, moderate: 2, major: 3, critical: 4, catastrophic: 4 };
        gravity = m[severity] || 2;
      } else {
        const m = { Faible: 1, Moyen: 2, Élevé: 3, Critique: 4, Catastrophique: 5,
                    minor: 1, moderate: 2, major: 3, critical: 4, catastrophic: 5 };
        gravity = m[severity] || 3;
      }
      for (const ap of (ss.attackPaths || [])) {
        const apId = typeof ap === 'string' ? ap : ap.id;
        const preliminary = (typeof ap === 'object' && ap.preliminary?.active) ? ap.preliminary : null;
        const opScenarios = (operationalScenarios || []).filter(op => op.attackPathId === apId);
        if (opScenarios.length === 0 && !preliminary) continue;
        const vraisemblance = preliminary
          ? preliminary.likelihood
          : Math.max(...opScenarios.map(op => Number(op.scenarioVraisemblance || op.vraisemblance || op.likelihood || 1)));
        results.push({
          id: apId,
          name: ap.name || apId,
          referenceCode: ap.referenceCode,
          gravity,
          likelihood: vraisemblance,
          attackPaths: [{ id: apId }],
          preliminary: !!preliminary,
          preliminaryJustification: preliminary?.justification || '',
        });
      }
    }
    return results;
  }, [strategicScenarios, operationalScenarios, riskLevelsCount]);

  const riskScenarioIndexMap = useMemo(() => {
    const map = {};
    let idx = 0;
    for (const ss of (strategicScenarios || [])) {
      const scenarioRef = ss.referenceCode || ss.id;
      for (const ap of (ss.attackPaths || [])) {
        const id = typeof ap === 'string' ? ap : ap.id;
        if (id && !map[id]) {
          map[id] = {
            shortRef: `R${++idx}`,
            originalRef: typeof ap === 'string' ? ap : (ap.referenceCode || ap.name || ap.id),
            name: typeof ap === 'string' ? '' : (ap.name || ''),
            scenarioRef,
          };
        }
      }
    }
    return map;
  }, [strategicScenarios]);

  const riskScenarios = useMemo(() => {
    const state = useAtelier5Store.getState();
    return state.getRiskScenarios();
  }, [strategicScenarios, operationalScenarios]);

  if (!riskScenarios.length) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-400">
        <Shield size={48} className="mb-4 opacity-30" />
        <p className="text-lg font-medium">No risk scenarios found</p>
        <p className="text-sm mt-1">Complete Workshops 3 and 4 to generate risk scenarios</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-4"
    >
      <h2 className="text-lg font-bold text-slate-800">{t('workshop5.synthesis.title', 'Synthèse des scénarios de risque')}</h2>

      <RiskScenarioRecapTable
        strategicScenarios={strategicScenarios}
        attackPathScenarios={attackPathScenarios}
        riskScenarioIndexMap={riskScenarioIndexMap}
        riskLevels={riskLevelsConfig}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <AttackPathRiskMatrix scenarios={attackPathScenarios} maxGravity={riskLevelsCount} t={t} riskScenarioIndexMap={riskScenarioIndexMap} riskLevels={riskLevelsConfig} />
        <ResidualRiskMatrix attackPathScenarios={attackPathScenarios} riskScenarios={riskScenarios} maxGravity={riskLevelsCount} t={t} riskScenarioIndexMap={riskScenarioIndexMap} riskLevels={riskLevelsConfig} />
      </div>
      <RiskScenarioLegend riskScenarioIndexMap={riskScenarioIndexMap} />

      <RiskAcceptanceTable levels={riskLevelsConfig} />

      <SecurityMeasuresTable strategicScenarios={strategicScenarios} riskScenarioIndexMap={riskScenarioIndexMap} />

      <ImplementationTimeline strategicScenarios={strategicScenarios} />

      <ResidualRiskCards
        attackPathScenarios={attackPathScenarios}
        riskScenarioIndexMap={riskScenarioIndexMap}
        strategicScenarios={strategicScenarios}
        riskScenarios={riskScenarios}
        riskLevelsCount={riskLevelsCount}
        riskLevels={riskLevelsConfig}
      />

      <RiskScenarioTreatmentTable
        strategicScenarios={strategicScenarios}
        attackPathScenarios={attackPathScenarios}
        riskScenarioIndexMap={riskScenarioIndexMap}
        riskLevels={riskLevelsConfig}
      />

      <ImplementationScheduleTable strategicScenarios={strategicScenarios} riskScenarioIndexMap={riskScenarioIndexMap} />

      <DreadedEventCoverage
        strategicScenarios={strategicScenarios}
        operationalScenarios={operationalScenarios}
        riskScenarioIndexMap={riskScenarioIndexMap}
      />

    </motion.div>
  );
};

export default RiskScenarioListView;
